import { MUI_POPOVER_MARGIN_THRESHOLD } from "@/components/ui/mui-field-shape";

// The layer under MUI's non-native `Select` — `Menu` → `Popover` → `Modal` —
// as both Atlas ports draw it: the single select (`mui-menu-select.tsx`, whose
// header reads each piece off MUI 9.2.0) and the multiple one
// (`mui-multi-select.tsx`). Its timings, the scroll lock, the paper's
// placement, the opening press's release test, the hiding of the page from
// assistive tech, and the list's typeahead.

export const UNSELECTED_MOUSE_UP_DELAY_MS = 200;
export const OPEN_TYPEAHEAD_RESET_MS = 500;
/** How far outside the field a release still counts as on it (`SelectInput.js:35`). */
const OPENING_MOUSE_UP_BOUNDARY_PX = 2;
/** `@mui/utils/debounce`'s default, which `Popover` takes for its resize. */
export const RESIZE_DEBOUNCE_MS = 166;

/** What opened the menu — MUI's `getOpenInteractionType`. */
export type OpenedBy = "pointer" | "keyboard" | null;

// `ModalManager`'s scroll lock (`ModalManager.js:40-80`, its `containers`
// list): **one lock on the body however many menus hold it** — the first to
// open takes the body's own styles and sets `overflow: hidden`, growing its
// right padding by the scrollbar that takes away (only when the page
// overflows); the last to close gives the styles back. Per-menu snapshots
// would let the first menu to close unlock the page under one still open, or
// restore a lock another had set.
let scrollLockHolders = 0;
let restoreScrollLock: (() => void) | null = null;

export function acquireScrollLock(): () => void {
  if (scrollLockHolders === 0) {
    const body = document.body;
    const previous = { overflow: body.style.overflow, paddingRight: body.style.paddingRight };
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbar > 0) {
      const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${padding + scrollbar}px`;
    }
    body.style.overflow = "hidden";
    restoreScrollLock = () => {
      body.style.overflow = previous.overflow;
      body.style.paddingRight = previous.paddingRight;
    };
  }
  scrollLockHolders += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    scrollLockHolders -= 1;
    if (scrollLockHolders === 0) {
      restoreScrollLock?.();
      restoreScrollLock = null;
    }
  };
}

/** `MenuList`'s open typeahead (`MenuList.js:203-238`): what it has typed so far. */
export interface MenuTypeahead {
  keys: string[];
  repeating: boolean;
  previousKeyMatched: boolean;
  lastTime: number;
}

export const freshTypeahead = (): MenuTypeahead => ({
  keys: [],
  repeating: true,
  previousKeyMatched: true,
  lastTime: 0,
});

/**
 * One printable key of `MenuList`'s typeahead, on a 500ms buffer: a run of one
 * letter cycles the items starting with it, anything else matches the typed
 * prefix, the focused item kept while it still matches; once a key matches
 * nothing, the rest of the run is let through.
 *
 * `at` is the item holding the focus (-1 for none) and `from` the index the
 * search steps on from. Hands back the index to focus, if any, and whether the
 * key was the typeahead's — the caller's `preventDefault`.
 */
export function typeaheadStep(
  criteria: MenuTypeahead,
  eventKey: string,
  now: number,
  items: readonly HTMLElement[],
  at: number,
  from: number = at,
): { target: number | null; handled: boolean } {
  const key = eventKey.toLowerCase();
  if (criteria.keys.length > 0) {
    if (now - criteria.lastTime > OPEN_TYPEAHEAD_RESET_MS) {
      criteria.keys = [];
      criteria.repeating = true;
      criteria.previousKeyMatched = true;
    } else if (criteria.repeating && key !== criteria.keys[0]) {
      criteria.repeating = false;
    }
  }
  criteria.lastTime = now;
  criteria.keys.push(key);
  const matches = (item: HTMLElement) => {
    const text = (item.textContent ?? "").trim().toLowerCase();
    if (text.length === 0) return false;
    return criteria.repeating
      ? text[0] === criteria.keys[0]
      : text.startsWith(criteria.keys.join(""));
  };
  const keep = at !== -1 && !criteria.repeating && matches(items[at]!);
  let target: number | null = null;
  if (criteria.previousKeyMatched && !keep) {
    for (let step = 1; step <= items.length; step += 1) {
      const index = (from + step + items.length) % items.length;
      if (matches(items[index]!)) {
        target = index;
        break;
      }
    }
  }
  const found = keep || target !== null;
  if (!(criteria.previousKeyMatched && found)) criteria.previousKeyMatched = false;
  return { target, handled: criteria.previousKeyMatched && found };
}

/**
 * `Popover`'s `getPositioningStyle` at the select's origins: the paper's
 * top-centre on the anchor's bottom-centre, at least the anchor's width, pushed
 * back inside the margin with its transform origin following (`Popover.js:170-232`).
 */
export function placePaper(paper: HTMLElement, anchor: HTMLElement | null) {
  if (!anchor) return;
  paper.style.minWidth = `${anchor.clientWidth}px`;
  const rect = anchor.getBoundingClientRect();
  const width = paper.offsetWidth;
  const height = paper.offsetHeight;
  const margin = MUI_POPOVER_MARGIN_THRESHOLD;
  const origin = { vertical: 0, horizontal: width / 2 };
  let top = rect.bottom - origin.vertical;
  let left = rect.left + rect.width / 2 - origin.horizontal;
  const heightThreshold = window.innerHeight - margin;
  const widthThreshold = window.innerWidth - margin;
  if (top < margin) {
    const diff = top - margin;
    top -= diff;
    origin.vertical += diff;
  } else if (top + height > heightThreshold) {
    const diff = top + height - heightThreshold;
    top -= diff;
    origin.vertical += diff;
  }
  if (left < margin) {
    const diff = left - margin;
    left -= diff;
    origin.horizontal += diff;
  } else if (left + width > widthThreshold) {
    const diff = left + width - widthThreshold;
    left -= diff;
    origin.horizontal += diff;
  }
  paper.style.top = `${Math.round(top)}px`;
  paper.style.left = `${Math.round(left)}px`;
  paper.style.transformOrigin = `${origin.horizontal}px ${origin.vertical}px`;
}

/**
 * `MenuList`'s `adjustStyleForScrollbar`, which `Menu` calls on entering: a
 * list taller than its paper is padded and widened by the scrollbar's size —
 * the *page's* scrollbar, as `getScrollbarSize` measures it — so the paper's
 * own scrollbar does not take its width out of the rows.
 */
export function makeRoomForScrollbar(paper: HTMLElement, list: HTMLElement | null) {
  if (!list || list.style.width) return;
  if (paper.clientHeight >= list.clientHeight) return;
  const size = `${window.innerWidth - document.documentElement.clientWidth}px`;
  list.style.paddingRight = size;
  list.style.width = `calc(100% + ${size})`;
}

/** `isMouseEventInsideElement` (`SelectInput.js:53-70`). */
export function releasedOn(event: MouseEvent, element: HTMLElement | null) {
  if (!element) return false;
  if (event.composedPath().includes(element)) return true;
  if (event.target instanceof Node && element.contains(event.target)) return true;
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return false;
  const pad = OPENING_MOUSE_UP_BOUNDARY_PX;
  return (
    event.clientX >= rect.left - pad &&
    event.clientX <= rect.right + pad &&
    event.clientY >= rect.top - pad &&
    event.clientY <= rect.bottom + pad
  );
}

/** Tags `ModalManager` never hides (`ModalManager.js`, `isAriaHiddenForbiddenOnElement`). */
const NEVER_HIDDEN = new Set([
  "TEMPLATE",
  "SCRIPT",
  "STYLE",
  "LINK",
  "MAP",
  "META",
  "NOSCRIPT",
  "PICTURE",
  "COL",
  "COLGROUP",
  "PARAM",
  "SLOT",
  "SOURCE",
  "TRACK",
]);

/**
 * `ariaHiddenSiblings`: every other top-level node, unless already hidden. A
 * menu portalled into a dialog's content is not top-level, so its
 * neighbours are hidden at every level up to the body — what MUI's, a
 * top-level node above the dialog, gets by hiding the dialog whole.
 */
export function hideSiblings(root: HTMLElement) {
  const hidden: Element[] = [];
  for (let node: HTMLElement = root; node !== document.body && node.parentElement;) {
    const parent: HTMLElement = node.parentElement;
    for (const sibling of parent.children) {
      if (sibling === node || sibling.getAttribute("aria-hidden") === "true") continue;
      if (NEVER_HIDDEN.has(sibling.tagName)) continue;
      if (sibling.tagName === "INPUT" && sibling.getAttribute("type") === "hidden") continue;
      sibling.setAttribute("aria-hidden", "true");
      hidden.push(sibling);
    }
    node = parent;
  }
  return hidden;
}
